import { useMemo } from 'react';

import { useFeatureFlagValue } from './useFeatureFlag';

import { FEATURE_FLAGS } from '@/constants/featureFlags';

export type ExpertGroupingVariant = 'normal' | 'grouped' | 'grouped_with_mdr';

type ParsedFlagValue = {
  variant: ExpertGroupingVariant;
  cancerTypes: string[];
};

export type UseExpertGroupingResult = {
  variant: ExpertGroupingVariant;
  shouldGroupSurgicalOncologists: boolean;
  showMultiDisciplinarySuggestion: boolean;
  isApplicable: boolean;
};

const VALID_VARIANTS: ExpertGroupingVariant[] = ['normal', 'grouped', 'grouped_with_mdr'];

/**
 * Parses and validates the feature flag value with defensive defaults.
 *
 * Uses type guards and fallbacks rather than runtime schema validation (e.g., Zod)
 * since the flag structure is simple and well-tested. Invalid or missing fields
 * safely default to 'normal' variant with no cancer types, ensuring the experiment
 * gracefully degrades rather than throwing errors.
 */
function parseFlagValue(flagValue: unknown): ParsedFlagValue {
  if (typeof flagValue === 'object' && flagValue !== null) {
    const obj = flagValue as Record<string, unknown>;

    const rawVariant = obj.variant;

    const variant: ExpertGroupingVariant =
      typeof rawVariant === 'string' && VALID_VARIANTS.includes(rawVariant as ExpertGroupingVariant)
        ? (rawVariant as ExpertGroupingVariant)
        : 'normal';

    const cancerTypes = Array.isArray(obj.cancerTypes)
      ? obj.cancerTypes.filter((t): t is string => typeof t === 'string')
      : [];

    return { variant, cancerTypes };
  }

  return { variant: 'normal', cancerTypes: [] };
}

function shouldGroupForCancerType(cancerType: string | undefined, cancerTypes: string[]): boolean {
  if (!cancerType || cancerTypes.length === 0) {
    return false;
  }

  const cancerTypeLower = cancerType.toLowerCase();

  return cancerTypes.some((type) => cancerTypeLower.includes(type.toLowerCase()));
}

/**
 * Hook to get the user's assigned variant for the expert grouping experiment.
 * Controls surgical oncologist grouping in expert selection dropdowns.
 * Variant is persisted per user via Flagsmith.
 *
 * Flag value format:
 * { "variant": "normal" | "grouped" | "grouped_with_mdr", "cancerTypes": ["breast", "lung"] }
 *
 * Variants:
 * - "normal": No surgical oncologist grouping
 * - "grouped": Surgical oncologists grouped at top of dropdown
 * - "grouped_with_mdr": Surgical oncologists grouped with multi-disciplinary review suggestion text
 *
 * @param cancerType - The cancer type for the current inquiry
 * @returns Object containing:
 *   - variant: The A/B/C experiment variant ('normal' | 'grouped' | 'grouped_with_mdr')
 *   - shouldGroupSurgicalOncologists: Whether to display surgical oncologists grouped at top
 *   - showMultiDisciplinarySuggestion: Whether to show multi-disciplinary review suggestion
 *   - isApplicable: Whether the experiment applies to this cancer type (for analytics)
 *
 * @example
 * const { variant, shouldGroupSurgicalOncologists, showMultiDisciplinarySuggestion, isApplicable } = useExpertGrouping(cancerType);
 * if (shouldGroupSurgicalOncologists) {
 *   // Partition and display surgical oncologists at top
 * }
 * // For analytics: isApplicable ? variant : 'not_applicable'
 */
export function useExpertGrouping(cancerType?: string): UseExpertGroupingResult {
  const flagValue = useFeatureFlagValue(FEATURE_FLAGS.EXPERT_SELECT_SURGEON_GROUPING);

  return useMemo(() => {
    const { variant, cancerTypes } = parseFlagValue(flagValue);

    const isApplicable = shouldGroupForCancerType(cancerType, cancerTypes);

    const shouldGroupSurgicalOncologists =
      (variant === 'grouped' || variant === 'grouped_with_mdr') && isApplicable;

    const showMultiDisciplinarySuggestion = variant === 'grouped_with_mdr' && isApplicable;

    return {
      variant,
      shouldGroupSurgicalOncologists,
      showMultiDisciplinarySuggestion,
      isApplicable,
    };
  }, [flagValue, cancerType]);
}
